import { ContextProvider } from '@/app/providers/ContextProvider';
import { store } from '@/app/store/store';
import { Username } from '@/features/users';
import { CHAT_CONSTANTS } from '@/shared/constants/chatConstants';
import storage from '@/shared/utils/storage';
import { renderToString } from 'react-dom/server';
import { Provider } from 'react-redux';

/**
 * Parse HTML content with mention chips into plain text and extract mentioned user IDs
 * This is the single source of truth for HTML parsing in the application
 *
 * @param {string} htmlContent - HTML string containing text and mention chips
 * @returns {{message: string, mentionUserIds: string[]}} Parsed message and mentioned user IDs
 */
export const parseHtmlMessage = htmlContent => {
   if (!htmlContent) return { message: '', mentionUserIds: [] };

   const parser = new DOMParser();
   const doc = parser.parseFromString(htmlContent, 'text/html');
   const nodes = doc.body.childNodes;
   let formattedMessage = '';
   const mentionUserIds = [];

   nodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
         formattedMessage += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
         if (node.classList.contains(CHAT_CONSTANTS.MENTION_CHIP_CLASS)) {
            formattedMessage += CHAT_CONSTANTS.MENTION_FORMAT;
            mentionUserIds.push(node.dataset.userId);
         } else if (node.tagName === 'DIV' || node.tagName === 'BR') {
            formattedMessage += CHAT_CONSTANTS.NEWLINE;
            formattedMessage += node.textContent;
         } else {
            formattedMessage += node.textContent;
         }
      }
   });

   return {
      message: formattedMessage.replace(new RegExp(CHAT_CONSTANTS.NON_BREAKING_SPACE, 'g'), ' ').trim(),
      mentionUserIds,
   };
};

/**
 * Parse HTML content from a contentEditable element directly
 * This version works with DOM nodes instead of HTML strings
 *
 * @param {HTMLElement} element - ContentEditable element to parse
 * @returns {{message: string, mentionUserIds: string[]}} Parsed message and mentioned user IDs
 */
export const parseContentEditableMessage = element => {
   if (!element) return { message: '', mentionUserIds: [] };

   const nodes = element.childNodes;
   let formattedMessage = '';
   const mentionUserIds = [];

   nodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
         formattedMessage += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
         if (node.classList.contains(CHAT_CONSTANTS.MENTION_CHIP_CLASS)) {
            formattedMessage += CHAT_CONSTANTS.MENTION_FORMAT;
            mentionUserIds.push(node.dataset.userId);
         } else if (node.tagName === 'DIV' || node.tagName === 'BR') {
            formattedMessage += CHAT_CONSTANTS.NEWLINE;
            formattedMessage += node.textContent;
         } else {
            formattedMessage += node.textContent;
         }
      }
   });

   return {
      message: formattedMessage.replace(new RegExp(CHAT_CONSTANTS.NON_BREAKING_SPACE, 'g'), ' ').trim(),
      mentionUserIds,
   };
};

/**
 * Convert plain text with @[?] placeholders and mention IDs into HTML with chips.
 * This is the inverse of parseHtmlMessage and is used for pre-filling inputs.
 *
 * @param {string} text - Plain text message with @[?] placeholders
 * @param {string[]} mentionIds - Array of user IDs corresponding to @[?] placeholders
 * @param {Object} usersMap - Map of user info from Redux store
 * @param {string} currentUserId - Current user's ID to handle "You" in chips
 * @returns {string} HTML string with mention chips
 */
export const formatMessageToHtml = (text = '', mentionIds = []) => {
   if (!text) return '';
   if (!mentionIds || mentionIds.length === 0) return text;

   let index = 0;
   const escapedMention = CHAT_CONSTANTS.MENTION_FORMAT.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
   const mentionRegex = new RegExp(escapedMention, 'g');

   return text.replace(mentionRegex, () => {
      const userId = mentionIds[index++];
      if (!userId) return CHAT_CONSTANTS.MENTION_FORMAT;

      // Render the Username component to a static HTML string.
      // This matches the mechanism in createRoot(chip).render(...) but for static draft generation.
      const componentHtml = renderToString(
         <Provider store={store}>
            <ContextProvider>
               <Username
                  userId={userId}
                  prefix={CHAT_CONSTANTS.CHIP_PREFIX}
                  isLocal={userId === storage.getItem('username')}
                  contentEditable="false"
                  suppressContentEditableWarning
               />
            </ContextProvider>
         </Provider>,
      );

      // We wrap the component output in the required mention-chip container.
      // Username renders an <AppText as="span">, but we need the outer span with the chip class
      // and data attributes for the contentEditable input logic to recognize it as a chip.
      return `<span class="${CHAT_CONSTANTS.MENTION_CHIP_CLASS}" contenteditable="false" data-user-id="${userId}" userId="${userId}">${componentHtml}</span>`;
   });
};
